import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../../services/api';
import ClientLayout from '../../components/ClientLayout';
import { Loader2, Calendar, Tag, X, ZoomIn } from 'lucide-react';

const ClientLooks = () => {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLook, setSelectedLook] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  
  const navigate = useNavigate();
  const clientId = localStorage.getItem('clientId');

  useEffect(() => {
    if (!clientId) {
      navigate('/cabinet/login');
      return;
    }
    fetchLooks();
  }, [clientId, navigate]);

  const fetchLooks = async () => {
    try {
      const data = await clientAPI.getLooks(clientId);
      setLooks(data);
    } catch (err) {
      console.error('Error fetching looks:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ClientLayout>
      <div className="looks-page" data-testid="looks-page">
        {/* Header */}
        <div className="looks-header">
          <h1 className="looks-title">Мои образы</h1>
          <p className="looks-count">
            {looks.length} {looks.length === 1 ? 'образ' : looks.length < 5 ? 'образа' : 'образов'}
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="loading-container">
            <Loader2 size={32} className="spinner" />
          </div>
        ) : looks.length === 0 ? (
          <div className="empty-state">
            <p className="empty-title">Пока нет образов</p>
            <p className="empty-text">Ваш стилист создаст образы на основе вашего гардероба</p>
          </div>
        ) : (
          <div className="looks-masonry-grid" data-testid="looks-grid">
            {looks.map((look) => (
              <div
                key={look.id}
                className="looks-masonry-item"
                onClick={() => setSelectedLook(look)}
                data-testid={`look-card-${look.id}`}
              >
                {/* Look Image */}
                {look.look_images?.[0] && (
                  <div className="look-image-container">
                    <img
                      src={look.look_images[0]}
                      alt={look.name}
                      className="look-image"
                    />
                    <div className="look-zoom-overlay">
                      <ZoomIn size={24} />
                    </div>
                  </div>
                )}

                <div className="look-info">
                  <h3 className="look-name">{look.name}</h3>

                  {look.description && (
                    <p className="look-description">{look.description}</p>
                  )}

                  <div className="look-tags">
                    {look.event_types?.slice(0, 3).map((event, idx) => (
                      <span key={idx} className="look-tag">{event}</span>
                    ))}
                  </div>

                  <div className="look-date">
                    <Calendar size={12} />
                    {new Date(look.created_at).toLocaleDateString('ru-RU')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Look Detail Modal */}
        {selectedLook && (
          <div className="look-modal-overlay" onClick={() => setSelectedLook(null)}>
            <div className="look-modal-content" onClick={(e) => e.stopPropagation()} data-testid="look-detail-modal">
              {/* Close button */}
              <button onClick={() => setSelectedLook(null)} className="look-modal-close">
                <X size={20} />
              </button>

              {/* Look Image */}
              {selectedLook.look_images?.[0] && (
                <div 
                  className="look-modal-image-container"
                  onClick={() => setFullscreenImage(selectedLook.look_images[0])}
                >
                  <img
                    src={selectedLook.look_images[0]}
                    alt={selectedLook.name}
                    className="look-modal-image"
                  />
                  <div className="look-zoom-overlay">
                    <ZoomIn size={32} />
                  </div>
                </div>
              )}

              <div className="look-modal-info">
                <h2 className="look-modal-title">{selectedLook.name}</h2>

                {selectedLook.description && (
                  <p className="look-modal-description">{selectedLook.description}</p>
                )}

                {/* Event Types */}
                {selectedLook.event_types?.length > 0 && (
                  <div className="look-modal-events">
                    <div className="look-modal-events-header">
                      <Tag size={16} style={{ color: 'var(--accent-gold)' }} />
                      <span>Подходит для</span>
                    </div>
                    <div className="look-modal-events-list">
                      {selectedLook.event_types.map((event, idx) => (
                        <span key={idx} className="look-modal-event-tag">{event}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Wardrobe Items in Look */}
                {selectedLook.wardrobe_items?.length > 0 && (
                  <div className="look-modal-items">
                    <span className="look-modal-items-label">
                      Вещи в образе ({selectedLook.wardrobe_items.length})
                    </span>
                    <div className="look-modal-items-grid">
                      {selectedLook.wardrobe_items.map((item) => (
                        <div key={item.id} className="look-modal-item">
                          <div className="look-modal-item-image">
                            <img src={item.image_url} alt={item.item_kind} />
                          </div>
                          <div className="look-modal-item-name">
                            <p>{item.item_kind}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="look-modal-footer">
                  <Calendar size={12} />
                  Создан {new Date(selectedLook.created_at).toLocaleDateString('ru-RU')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fullscreen Image Viewer */}
        {fullscreenImage && (
          <div className="fullscreen-viewer" onClick={() => setFullscreenImage(null)}>
            <button className="fullscreen-close">
              <X size={32} />
            </button>
            <img 
              src={fullscreenImage} 
              alt="Full size" 
              className="fullscreen-image"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <style>{`
          .looks-page {
            max-width: 1200px;
            padding: 0 var(--space-m);
          }
          
          .looks-header {
            margin-bottom: var(--space-xl);
          }
          
          .looks-title {
            font-family: var(--font-display);
            font-size: clamp(1.5rem, 4vw, 2.5rem);
            font-weight: 300;
            margin-bottom: var(--space-s);
          }
          
          .looks-count {
            font-family: var(--font-body);
            color: var(--mid-gray);
            font-size: 0.875rem;
          }
          
          .loading-container {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 300px;
          }
          
          .spinner {
            color: var(--accent-gold);
            animation: spin 1s linear infinite;
          }
          
          .empty-state {
            text-align: center;
            padding: var(--space-xxxl);
            background-color: var(--white);
            border: 1px solid var(--soft-gray);
          }
          
          .empty-title {
            font-family: var(--font-display);
            font-size: 1.5rem;
            margin-bottom: var(--space-m);
          }
          
          .empty-text {
            font-family: var(--font-body);
            font-size: 0.875rem;
            color: var(--mid-gray);
          }
          
          /* Masonry Grid */
          .looks-masonry-grid {
            columns: 1;
            column-gap: var(--space-l);
          }
          
          @media (min-width: 500px) {
            .looks-masonry-grid {
              columns: 2;
            }
          }
          
          @media (min-width: 900px) {
            .looks-masonry-grid {
              columns: 3;
            }
          }
          
          .looks-masonry-item {
            break-inside: avoid;
            margin-bottom: var(--space-l);
            background-color: var(--white);
            border: 1px solid var(--soft-gray);
            cursor: pointer;
            overflow: hidden;
            transition: all 300ms ease;
          }
          
          .looks-masonry-item:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          }
          
          .look-image-container {
            position: relative;
            overflow: hidden;
            background-color: var(--cream);
          }
          
          .look-image {
            width: 100%;
            height: auto;
            display: block;
          }
          
          .look-zoom-overlay {
            position: absolute;
            inset: 0;
            background: rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 200ms ease;
            color: white;
          }
          
          .look-image-container:hover .look-zoom-overlay,
          .look-modal-image-container:hover .look-zoom-overlay {
            opacity: 1;
          }
          
          .look-info {
            padding: var(--space-l);
          }
          
          .look-name {
            font-family: var(--font-display);
            font-size: 1.125rem;
            font-weight: 400;
            margin-bottom: var(--space-s);
          }
          
          .look-description {
            font-family: var(--font-body);
            font-size: 0.875rem;
            color: var(--mid-gray);
            margin-bottom: var(--space-m);
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          .look-tags {
            display: flex;
            flex-wrap: wrap;
            gap: var(--space-s);
            margin-bottom: var(--space-m);
          }
          
          .look-tag {
            padding: 4px 12px;
            background-color: var(--cream);
            font-family: var(--font-body);
            font-size: 0.75rem;
            color: var(--charcoal);
          }
          
          .look-date {
            display: flex;
            align-items: center;
            gap: var(--space-s);
            font-size: 0.75rem;
            color: var(--light-gray);
          }
          
          /* Modal */
          .look-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0,0,0,0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--space-l);
            z-index: 1000;
            overflow-y: auto;
          }
          
          .look-modal-content {
            background-color: var(--white);
            max-width: 800px;
            width: 100%;
            max-height: 90vh;
            overflow: auto;
            position: relative;
          }
          
          .look-modal-close {
            position: absolute;
            top: var(--space-m);
            right: var(--space-m);
            background-color: var(--white);
            border: none;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            z-index: 10;
          }
          
          .look-modal-image-container {
            position: relative;
            cursor: pointer;
            background-color: var(--cream);
          }
          
          .look-modal-image {
            width: 100%;
            height: auto;
            display: block;
          }
          
          .look-modal-info {
            padding: var(--space-xxl);
          }
          
          .look-modal-title {
            font-family: var(--font-display);
            font-size: 2rem;
            font-weight: 300;
            margin-bottom: var(--space-m);
          }
          
          .look-modal-description {
            font-family: var(--font-body);
            font-size: 1rem;
            color: var(--charcoal);
            line-height: 1.7;
            margin-bottom: var(--space-xl);
          }
          
          .look-modal-events {
            margin-bottom: var(--space-xl);
          }
          
          .look-modal-events-header {
            display: flex;
            align-items: center;
            gap: var(--space-s);
            margin-bottom: var(--space-m);
            font-family: var(--font-body);
            font-size: 0.75rem;
            letter-spacing: 1px;
            text-transform: uppercase;
            color: var(--mid-gray);
          }
          
          .look-modal-events-list {
            display: flex;
            flex-wrap: wrap;
            gap: var(--space-s);
          }
          
          .look-modal-event-tag {
            padding: 8px 16px;
            background-color: var(--cream);
            font-family: var(--font-body);
            font-size: 0.875rem;
            color: var(--charcoal);
          }
          
          .look-modal-items {
            margin-bottom: var(--space-xl);
          }
          
          .look-modal-items-label {
            font-family: var(--font-body);
            font-size: 0.75rem;
            letter-spacing: 1px;
            text-transform: uppercase;
            color: var(--mid-gray);
            display: block;
            margin-bottom: var(--space-m);
          }
          
          .look-modal-items-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
            gap: var(--space-m);
          }
          
          .look-modal-item {
            border: 1px solid var(--soft-gray);
          }
          
          .look-modal-item-image {
            aspect-ratio: 1;
            overflow: hidden;
            background-color: var(--cream);
          }
          
          .look-modal-item-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          .look-modal-item-name {
            padding: var(--space-s);
          }
          
          .look-modal-item-name p {
            font-family: var(--font-body);
            font-size: 0.75rem;
            text-transform: capitalize;
            text-align: center;
          }
          
          .look-modal-footer {
            margin-top: var(--space-xl);
            padding-top: var(--space-l);
            border-top: 1px solid var(--soft-gray);
            display: flex;
            align-items: center;
            gap: var(--space-s);
            font-size: 0.75rem;
            color: var(--light-gray);
          }
          
          /* Fullscreen Viewer */
          .fullscreen-viewer {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0,0,0,0.95);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2000;
            padding: var(--space-l);
          }
          
          .fullscreen-close {
            position: absolute;
            top: var(--space-l);
            right: var(--space-l);
            background: none;
            border: none;
            color: white;
            cursor: pointer;
            padding: var(--space-s);
          }
          
          .fullscreen-image {
            max-width: 100%;
            max-height: 90vh;
            object-fit: contain;
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </ClientLayout>
  );
};

export default ClientLooks;
